import React, { useState, useEffect } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import Toast from '../../components/Toast/Toast';
import { getTopSymbols } from '../../services/SymbolsService';
import {
    getActive, getMinBudget, getAccess,
    start as startSession, stop as stopSession, sellNow as sellNowSession,
    getTemplates, updateTemplate, startTemplate, pauseTemplate
} from '../../services/FuturesTraderService';
import { useIntl } from 'react-intl';
import logger from '../../utils/logger';
import { fmtMoney, fmtPct, fmtPrice } from '../../utils/format';

const LEVERAGES = [2, 5, 10];

function FuturesTrader() {

    const { formatMessage } = useIntl();
    const t = (id) => formatMessage({ id });

    const [topPairs, setTopPairs] = useState([]);
    const [positions, setPositions] = useState([]);      // active futures positions/sessions
    const [minBudgets, setMinBudgets] = useState({});    // symbol -> minBudget
    const [templates, setTemplates] = useState([]);      // system-default templates (PAUSED)
    const [access, setAccess] = useState(null);          // { enabled, reason, quoteBalance }
    const [confirm, setConfirm] = useState(null);        // { payload, tpl } LIVE confirmation
    const [editTpl, setEditTpl] = useState(null);        // template being edited (modal)
    const [notification, setNotification] = useState({});

    useEffect(() => {
        loadAccess();
        loadTemplates();
        getTopSymbols(8, 'USDC', 'volatile')
            .then(result => {
                const pairs = Array.isArray(result) ? result : [];
                setTopPairs(pairs);
                pairs.forEach(pr => getMinBudget(pr.symbol)
                    .then(mb => setMinBudgets(prev => ({ ...prev, [pr.symbol]: mb.minBudget })))
                    .catch(() => { }));
            })
            .catch(err => logger.error(err.response ? err.response.data : err.message));
        refreshPositions();
        const timer = setInterval(refreshPositions, 5000);
        return () => clearInterval(timer);
    }, []);

    function loadAccess() {
        getAccess()
            .then(setAccess)
            .catch(err => logger.error(err.response ? err.response.data : err.message));
    }

    function refreshPositions() {
        getActive()
            .then(result => setPositions(Array.isArray(result) ? result : []))
            .catch(err => logger.error(err.response ? err.response.data : err.message));
    }

    function loadTemplates() {
        getTemplates()
            .then(result => setTemplates(Array.isArray(result) ? result : []))
            .catch(err => logger.error(err.response ? err.response.data : err.message));
    }

    const liveDisabled = access && access.enabled === false;

    function sideColor(side) {
        return side === 'SHORT' ? 'danger' : 'success';
    }

    function statusColor(status) {
        switch (status) {
            case 'RUNNING': return 'success';
            case 'PAUSED': return 'info';
            case 'STOPPED': return 'secondary';
            default: return 'secondary';
        }
    }

    function onSaveTemplate(tpl) {
        const payload = {
            symbol: tpl.symbol,
            budget: parseFloat(tpl.budget),
            leverage: parseInt(tpl.leverage),
            marginType: tpl.marginType,
            side: tpl.side,
            maxTrades: parseInt(tpl.maxTrades),
            takeProfitPct: parseFloat(tpl.takeProfitPct),
            stopLossPct: parseFloat(tpl.stopLossPct),
            mode: tpl.mode
        };
        updateTemplate(tpl.id, payload)
            .then(() => { setNotification({ type: 'success', text: t('futures.saved') }); setEditTpl(null); loadTemplates(); })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }));
    }

    function onStartTemplate(tpl) {
        if (tpl.mode === 'LIVE') {
            if (liveDisabled) return setNotification({ type: 'error', text: (access && access.reason) || t('futures.notEnabled') });
            return setConfirm({ tpl, payload: { id: tpl.id } });
        }
        doStartTemplate(tpl.id);
    }

    function doStartTemplate(id) {
        setConfirm(null);
        startTemplate(id, {})
            .then(() => { setNotification({ type: 'success', text: t('futures.started') }); refreshPositions(); loadTemplates(); })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }));
    }

    function onPauseTemplate(id) {
        pauseTemplate(id)
            .then(() => { setNotification({ type: 'success', text: t('futures.stopped') }); loadTemplates(); refreshPositions(); })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }));
    }

    function onStop(id) {
        stopSession(id)
            .then(() => { setNotification({ type: 'success', text: t('futures.stopped') }); refreshPositions(); })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }));
    }

    function onSellNow(id) {
        sellNowSession(id)
            .then(() => { setNotification({ type: 'success', text: t('futures.stopped') }); refreshPositions(); })
            .catch(err => setNotification({ type: 'error', text: err.response ? err.response.data : err.message }));
    }

    const notional = (tpl) => fmtMoney(parseFloat(tpl.budget || 0) * parseInt(tpl.leverage || 1));

    return (
        <React.Fragment>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h1 className="h4">{t('futures.title')}</h1>
                        <p className="mb-0">{t('futures.subtitle')}</p>
                    </div>
                </div>

                {/* Leverage / high-risk warning banner */}
                <div className="alert alert-warning border-0 shadow-sm" role="alert">
                    <strong>{t('futures.leverageWarn')}</strong>
                    <div className="small mt-1">{t('futures.eeaWarn')}</div>
                </div>

                {liveDisabled && (
                    <div className="alert alert-danger border-0 shadow-sm" role="alert">
                        <strong>{t('futures.notEnabled')}</strong>
                        {access && access.reason && <div className="small mt-1">{access.reason}</div>}
                    </div>
                )}

                {topPairs && topPairs.length > 0 && (
                    <div className="card card-body border-0 shadow mb-4">
                        <small className="text-muted text-uppercase d-block mb-2">{t('riskProfiles.topPairsToday')}</small>
                        <div className="d-flex flex-wrap gap-2">
                            {topPairs.map(p => (
                                <span key={p.symbol} className="badge bg-primary d-inline-flex align-items-center" style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}>
                                    <strong className="me-2">{p.symbol}</strong>
                                    <span className="me-2">{fmtPrice(p.close)}</span>
                                    <span className={p.changePct >= 0 ? 'text-success' : 'text-danger'}>
                                        {p.changePct >= 0 ? '+' : ''}{fmtPct(p.changePct)}%
                                    </span>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* System default TEMPLATE cards */}
                {templates && templates.length > 0 && (
                    <React.Fragment>
                        <div className="py-2">
                            <h5 className="mb-1">{t('risk.template.heading')}</h5>
                            <p className="mb-0 text-muted small">{t('risk.template.subtitle')}</p>
                        </div>
                        <div className="gh-grid gh-grid-wide mb-4">
                            {templates.map(tpl => (
                                <div key={tpl.id}>
                                    <div className="card border-secondary shadow h-100">
                                        <div className="card-header d-flex align-items-center justify-content-between">
                                            <h5 className="mb-0">{tpl.templateName || tpl.symbol}</h5>
                                            <div>
                                                <span className={`badge bg-${sideColor(tpl.side)} me-1`}>
                                                    {tpl.side === 'SHORT' ? t('futures.short') : t('futures.long')}
                                                </span>
                                                {tpl.status === 'PAUSED' && <span className="badge bg-info">{t('futures.paused')}</span>}
                                            </div>
                                        </div>
                                        <div className="card-body">
                                            {tpl.isSystemDefault && <span className="badge bg-secondary mb-3 d-block text-wrap">{t('futures.systemDefault')}</span>}
                                            <div className="d-flex justify-content-between"><small className="text-muted">{t('risk.pair')}</small><span className="fw-bold">{tpl.symbol}</span></div>
                                            <div className="d-flex justify-content-between"><small className="text-muted">{t('risk.amount')}</small><span>{fmtMoney(tpl.budget)} {tpl.quote || 'USDC'}</span></div>
                                            <div className="d-flex justify-content-between"><small className="text-muted">{t('futures.leverage')}</small><span className="fw-bold">{tpl.leverage}x</span></div>
                                            <div className="d-flex justify-content-between"><small className="text-muted">{t('futures.notional')}</small><span>{notional(tpl)} {tpl.quote || 'USDC'}</span></div>
                                            <div className="d-flex justify-content-between"><small className="text-muted">{t('futures.marginType')}</small><span>{tpl.marginType === 'CROSSED' ? t('futures.cross') : t('futures.isolated')}</span></div>
                                            <div className="d-flex justify-content-between"><small className="text-muted">{t('riskProfiles.maxDailyTrades')}</small><span>{tpl.maxTrades}</span></div>
                                            <div className="d-flex justify-content-between"><small className="text-muted">{t('riskProfiles.takeProfit')} / {t('riskProfiles.stopLoss')}</small><span><span className="text-success">+{fmtPct(tpl.takeProfitPct)}%</span> / <span className="text-danger">−{fmtPct(tpl.stopLossPct)}%</span></span></div>
                                            <div className="d-flex justify-content-between"><small className="text-muted">{t('risk.mode')}</small><span className={`badge bg-${tpl.mode === 'LIVE' ? 'danger' : 'secondary'}`}>{tpl.mode}</span></div>
                                        </div>
                                        <div className="card-footer d-flex justify-content-between">
                                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setEditTpl({ ...tpl })}>{t('futures.edit')}</button>
                                            <div>
                                                {tpl.status === 'RUNNING' ? (
                                                    <button className="btn btn-sm btn-warning" onClick={() => onPauseTemplate(tpl.id)}>{t('futures.paused')}</button>
                                                ) : (
                                                    <button className="btn btn-sm btn-primary" onClick={() => onStartTemplate(tpl)}>{t('futures.start')}</button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </React.Fragment>
                )}

                {/* Active positions */}
                <div className="py-2">
                    <h5 className="mb-1">{t('futures.positionsTitle')}</h5>
                </div>
                <div className="card card-body border-0 shadow mb-4">
                    {positions && positions.length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0 gh-table-stack">
                                <thead>
                                    <tr>
                                        <th className="border-bottom">{t('risk.pair')}</th>
                                        <th className="border-bottom">{t('futures.short')}/{t('futures.long')}</th>
                                        <th className="border-bottom num">{t('futures.entryPrice')}</th>
                                        <th className="border-bottom num">{t('futures.leverage')}</th>
                                        <th className="border-bottom num">{t('futures.liqPrice')}</th>
                                        <th className="border-bottom num">{t('risk.trades')}</th>
                                        <th className="border-bottom num">{t('futures.pnl')}</th>
                                        <th className="border-bottom">{t('common.status')}</th>
                                        <th className="border-bottom text-end">{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {positions.map(pos => (
                                        <tr key={pos.id}>
                                            <td data-label={t('risk.pair')} className="fw-bold">{pos.symbol}</td>
                                            <td data-label={`${t('futures.short')}/${t('futures.long')}`}><span className={`badge bg-${sideColor(pos.side)}`}>{pos.side === 'SHORT' ? t('futures.short') : t('futures.long')}</span></td>
                                            <td data-label={t('futures.entryPrice')} className="num">{pos.entryPrice ? fmtPrice(pos.entryPrice) : (pos.openEntry ? fmtPrice(pos.openEntry) : '-')}</td>
                                            <td data-label={t('futures.leverage')} className="num">{pos.leverage}x</td>
                                            <td data-label={t('futures.liqPrice')} className="num gh-neg fw-bold">{pos.liquidationPrice ? fmtPrice(pos.liquidationPrice) : '-'}</td>
                                            <td data-label={t('risk.trades')} className="num">{pos.tradesCount}/{pos.maxTrades} ({pos.winCount || 0}W/{pos.lossCount || 0}L)</td>
                                            <td data-label={t('futures.pnl')} className={`num ${parseFloat(pos.pnl) >= 0 ? 'gh-pos fw-bold' : 'gh-neg fw-bold'}`}>{pos.pnl != null ? fmtMoney(pos.pnl) : '0.00'} {pos.quote || 'USDC'}</td>
                                            <td data-label={t('common.status')}><span className={`badge bg-${statusColor(pos.status)}`}>{pos.status}</span></td>
                                            <td data-label={t('common.actions')} className="text-end">
                                                <button className="btn btn-sm btn-outline-danger me-1" onClick={() => onStop(pos.id)}>{t('futures.stop')}</button>
                                                <button className="btn btn-sm btn-danger" onClick={() => onSellNow(pos.id)}>{t('futures.sellNow')}</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="mb-0 text-muted">{t('futures.noPositions')}</p>
                    )}
                </div>

                <Footer />
            </main>

            {/* LIVE confirm modal */}
            {confirm && (
                <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.6)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title text-danger">{t('futures.live')} — {t('risk.confirmTitle')}</h5>
                                <button type="button" className="btn-close" onClick={() => setConfirm(null)}></button>
                            </div>
                            <div className="modal-body">
                                <p className="text-danger fw-bold">{t('futures.leverageWarn')}</p>
                                <p>{t('futures.liveConfirm')}</p>
                                <ul className="mb-0">
                                    <li>{t('risk.pair')}: <strong>{confirm.tpl.symbol}</strong> · <span className={`text-${sideColor(confirm.tpl.side)}`}>{confirm.tpl.side === 'SHORT' ? t('futures.short') : t('futures.long')}</span></li>
                                    <li>{t('futures.margin')}: <strong>{fmtMoney(confirm.tpl.budget)} {confirm.tpl.quote || 'USDC'}</strong></li>
                                    <li>{t('futures.leverage')}: <strong>{confirm.tpl.leverage}x</strong></li>
                                    <li>{t('futures.notional')}: <strong>{notional(confirm.tpl)} {confirm.tpl.quote || 'USDC'}</strong></li>
                                    <li className="text-warning">{t('futures.eeaWarn')}</li>
                                </ul>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setConfirm(null)}>{t('common.cancel')}</button>
                                <button className="btn btn-danger" onClick={() => doStartTemplate(confirm.payload.id)}>{t('risk.confirmStart')}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit template modal */}
            {editTpl && (
                <div className="modal d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.6)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{t('futures.edit')} — {editTpl.templateName || editTpl.symbol}</h5>
                                <button type="button" className="btn-close" onClick={() => setEditTpl(null)}></button>
                            </div>
                            <div className="modal-body">
                                <span className="badge bg-secondary mb-3">{t('futures.systemDefault')}</span>
                                <div className="mb-2">
                                    <label className="form-label small text-muted">{t('risk.pair')}</label>
                                    <select className="form-select form-select-sm" value={editTpl.symbol} onChange={e => setEditTpl({ ...editTpl, symbol: e.target.value })}>
                                        {!topPairs.some(p => p.symbol === editTpl.symbol) && <option value={editTpl.symbol}>{editTpl.symbol}</option>}
                                        {topPairs.map(p => <option key={p.symbol} value={p.symbol}>{p.symbol} ({fmtPct(p.volatilityPct)}% vol)</option>)}
                                    </select>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label small text-muted">{t('common.side')}</label>
                                    <div className="btn-group w-100" role="group">
                                        <button type="button" className={`btn btn-sm ${editTpl.side === 'LONG' ? 'btn-success' : 'btn-secondary'}`} onClick={() => setEditTpl({ ...editTpl, side: 'LONG' })}>{t('futures.long')}</button>
                                        <button type="button" className={`btn btn-sm ${editTpl.side === 'SHORT' ? 'btn-danger' : 'btn-secondary'}`} onClick={() => setEditTpl({ ...editTpl, side: 'SHORT' })}>{t('futures.short')}</button>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-7 mb-2">
                                        <label className="form-label small text-muted">{t('futures.margin')} (USDC)</label>
                                        <input className="form-control form-control-sm" type="number" min={minBudgets[editTpl.symbol] || 1} step="1" value={editTpl.budget} onChange={e => setEditTpl({ ...editTpl, budget: e.target.value })} />
                                        {minBudgets[editTpl.symbol] && <small className="text-muted">{t('futures.minPerTrade')} {minBudgets[editTpl.symbol]} USDC</small>}
                                    </div>
                                    <div className="col-5 mb-2">
                                        <label className="form-label small text-muted">{t('riskProfiles.maxDailyTrades')}</label>
                                        <input className="form-control form-control-sm" type="number" min="1" max="100" value={editTpl.maxTrades} onChange={e => setEditTpl({ ...editTpl, maxTrades: e.target.value })} />
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label small text-muted">{t('futures.leverage')}</label>
                                    <div className="btn-group w-100" role="group">
                                        {LEVERAGES.map(lev => (
                                            <button key={lev} type="button" className={`btn btn-sm ${parseInt(editTpl.leverage) === lev ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setEditTpl({ ...editTpl, leverage: lev })}>{lev}x</button>
                                        ))}
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <label className="form-label small text-muted">{t('futures.marginType')}</label>
                                    <div className="btn-group w-100" role="group">
                                        <button type="button" className={`btn btn-sm ${editTpl.marginType === 'ISOLATED' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setEditTpl({ ...editTpl, marginType: 'ISOLATED' })}>{t('futures.isolated')}</button>
                                        <button type="button" className={`btn btn-sm ${editTpl.marginType === 'CROSSED' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setEditTpl({ ...editTpl, marginType: 'CROSSED' })}>{t('futures.cross')}</button>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-6 mb-2">
                                        <label className="form-label small text-muted">{t('riskProfiles.takeProfit')} %</label>
                                        <input className="form-control form-control-sm" type="number" min="0.3" max="500" step="0.1" value={editTpl.takeProfitPct} onChange={e => setEditTpl({ ...editTpl, takeProfitPct: e.target.value })} />
                                    </div>
                                    <div className="col-6 mb-2">
                                        <label className="form-label small text-muted">{t('riskProfiles.stopLoss')} %</label>
                                        <input className="form-control form-control-sm" type="number" min="0.3" max="100" step="0.1" value={editTpl.stopLossPct} onChange={e => setEditTpl({ ...editTpl, stopLossPct: e.target.value })} />
                                    </div>
                                </div>
                                <p className="small text-muted mb-2">{t('futures.notional')}: <strong>{notional(editTpl)} USDC</strong></p>
                                <span className="gh-badge gh-badge-danger">{t('futures.live')}</span>
                                {liveDisabled && <p className="small text-danger mt-2 mb-0">{(access && access.reason) || t('futures.notEnabled')}</p>}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setEditTpl(null)}>{t('common.cancel')}</button>
                                <button className="btn btn-primary" onClick={() => onSaveTemplate(editTpl)}>{t('futures.save')}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    );
}

export default FuturesTrader;
